import { Privacy } from 'services/PrivacyService';
import { RecentSearches } from 'services/RecentSearchService';
import { AbstractDeviceIntegration } from 'config/platforms/AbstractDeviceIntegration';
import { ParentalPinData } from 'services/ParentalPinService';
import { LoginType } from './analytics/MParticle';
import { LocalHistory } from './history/local/AbstractLocalHistoryService';
import { constants } from 'aliases';
import { BlueconicStorage } from './BlueconicService';
import { OneTrustJwtStorage } from './OneTrustService';

// known keys
const APP_VERSION_KEY = 'appVersion';
const CACHE_KEY = constants.brandTokens.cacheKey;
const HISTORY_KEY = 'history';
const RECENT_SEARCHES_KEY = 'recentSearches';
const PRIVACY_KEY = 'privacy';
const AUTOPLAY_KEY = 'autoplay';
const SUBTITLE_KEY = 'subtitle';
const TERMS_ACCEPTED_KEY = 'terms-accepted';
const LOGIN_REGISTRATION_KEY = 'login-registration-shown';
const AUDIO_KEY = 'audio';
const SHARE_HISTORY_WITH_TIZEN_KEY = 'share-history-with-tizen';
const PARENTAL_PIN_KEY = 'parental-pin';
const OFFLINE_ERROR_KEY = 'offline-error';
const LOGIN_TYPE_KEY = 'lr-login-type';
const BLUECONIC_KEY = 'blueconic-id';
const ONETRUST_JWT_KEY = 'onetrust-jwt';
const ONETRUST_ANONYMOUS_ID_KEY = 'onetrust-anonymous-id';
const ONETRUST_IDENTIFIER_KEY = 'onetrust-identifier';

// when key is known
interface StorageMethodStrict<T> {
  get: () => T | null;
  set: (value: T) => void;
  remove: () => void;
}

// when storage key has specific storage space. Ex. Conviva.sdkConfig
interface StorageMethodWithStorageSpace<T> {
  get: (storageSpace: string, key: string) => T | null;
  set: (storageSpace: string, key: string, value: T) => void;
  remove: (storageSpace: string, key: string) => void;
}

type LazyGetFunction<T> = () => (key: string) => T | null | undefined;
type LazySetFunction<T> = () => (key: string, value: T | null) => void;
type LazyRemoveFunction = () => (key: string) => void;

export default class StorageService {
  private readonly deviceIntegration: AbstractDeviceIntegration;

  private readonly lazyGetLocal = () => this.deviceIntegration.getLocalStorage;
  private readonly lazySetLocal = () => this.deviceIntegration.setLocalStorage;
  private readonly lazyRemoveLocal = () =>
    this.deviceIntegration.removeLocalStorage;

  private readonly lazyGetSession = () =>
    this.deviceIntegration.getSessionStorage;
  private readonly lazySetSession = () =>
    this.deviceIntegration.setSessionStorage;
  private readonly lazyRemoveSession = () =>
    this.deviceIntegration.removeSessionStorage;

  constructor(deviceIntegration: AbstractDeviceIntegration) {
    this.deviceIntegration = deviceIntegration;
  }

  appVersion: StorageMethodStrict<string> =
    this.createLocalStorageMethodStrict<string>(APP_VERSION_KEY);

  privacy: StorageMethodStrict<Privacy> =
    this.createLocalStorageMethodStrict<Privacy>(PRIVACY_KEY);

  cache: StorageMethodStrict<any> =
    this.createSessionStorageMethodStrict<any>(CACHE_KEY);

  history: StorageMethodStrict<LocalHistory> =
    this.createLocalStorageMethodStrict<LocalHistory>(HISTORY_KEY);

  recentSearches: StorageMethodStrict<RecentSearches> =
    this.createLocalStorageMethodStrict<RecentSearches>(RECENT_SEARCHES_KEY);

  autoplaySettings: StorageMethodStrict<boolean> =
    this.createLocalStorageMethodStrict<boolean>(AUTOPLAY_KEY);

  /**
   * Returns the autoplay setting with a default value of true if unset
   */
  shouldAutoplay(): boolean {
    return this.autoplaySettings.get() ?? true;
  }

  offlineError: StorageMethodStrict<boolean> =
    this.createLocalStorageMethodStrict<boolean>(OFFLINE_ERROR_KEY);

  subtitle: StorageMethodStrict<string> =
    this.createLocalStorageMethodStrict<string>(SUBTITLE_KEY);

  termsAcceptedTimestamp: StorageMethodStrict<number> =
    this.createLocalStorageMethodStrict(TERMS_ACCEPTED_KEY);

  loginRegistrationShownTimestamp: StorageMethodStrict<number> =
    this.createLocalStorageMethodStrict<number>(LOGIN_REGISTRATION_KEY);

  audio: StorageMethodStrict<string> =
    this.createLocalStorageMethodStrict<string>(AUDIO_KEY);

  shareHistoryWithTizen: StorageMethodStrict<boolean> =
    this.createLocalStorageMethodStrict<boolean>(SHARE_HISTORY_WITH_TIZEN_KEY);

  parentalPin: StorageMethodStrict<ParentalPinData> =
    this.createLocalStorageMethodStrict<ParentalPinData>(PARENTAL_PIN_KEY);

  loginType: StorageMethodStrict<LoginType> =
    this.createLocalStorageMethodStrict<LoginType>(LOGIN_TYPE_KEY);

  blueconic: StorageMethodStrict<BlueconicStorage> =
    this.createLocalStorageMethodStrict<BlueconicStorage>(BLUECONIC_KEY);

  oneTrustJwt: StorageMethodStrict<OneTrustJwtStorage> =
    this.createLocalStorageMethodStrict<OneTrustJwtStorage>(ONETRUST_JWT_KEY);

  oneTrustAnonymousId: StorageMethodStrict<string> =
    this.createLocalStorageMethodStrict<string>(ONETRUST_ANONYMOUS_ID_KEY);

  oneTrustIdentifier: StorageMethodStrict<string> =
    this.createLocalStorageMethodStrict<string>(ONETRUST_IDENTIFIER_KEY);

  clearSession() {
    // Explicitly clear session storage since it uses local storage when session
    // storage is unavailable
    this.cache.remove();
    this.deviceIntegration.clearSessionStorage();
  }

  // Storage for values with Strict value type
  private createGenericStorageMethodStrict<T>(
    key: string,
    getMethod: LazyGetFunction<T>,
    setMethod: LazySetFunction<T>,
    removeMethod: LazyRemoveFunction,
  ): StorageMethodStrict<T> {
    return {
      get: (): T | null => {
        return getMethod()(key) ?? null;
      },
      set: (value: T) => {
        setMethod()(key, value);
      },
      remove: () => {
        removeMethod()(key);
      },
    };
  }

  private createLocalStorageMethodStrict<T>(
    key: string,
  ): StorageMethodStrict<T> {
    return this.createGenericStorageMethodStrict<T>(
      key,
      this.lazyGetLocal,
      this.lazySetLocal,
      this.lazyRemoveLocal,
    );
  }

  // Storage for values with Strict value type
  private createSessionStorageMethodStrict<T>(
    key: string,
  ): StorageMethodStrict<T> {
    return this.createGenericStorageMethodStrict<T>(
      key,
      this.lazyGetSession,
      this.lazySetSession,
      this.lazyRemoveSession,
    );
  }

  // Storage for values with Strict value type Storage Space version. Ex. key: Conviva.sdkConfig
  private createGenericStorageMethodWithStorageSpace<T>(
    getMethod: LazyGetFunction<T>,
    setMethod: LazySetFunction<T>,
    removeMethod: LazyRemoveFunction,
  ): StorageMethodWithStorageSpace<T> {
    return {
      get: (storageSpace: string, key: string): T | null => {
        return getMethod()(storageSpace + '.' + key) ?? null;
      },
      set: (storageSpace: string, key: string, value: T) => {
        setMethod()(storageSpace + '.' + key, value);
      },
      remove: (storageSpace: string, key: string) => {
        removeMethod()(storageSpace + '.' + key);
      },
    };
  }

  private createLocalStorageMethodWithStorageSpace<
    T,
  >(): StorageMethodWithStorageSpace<T> {
    return this.createGenericStorageMethodWithStorageSpace<T>(
      this.lazyGetLocal,
      this.lazySetLocal,
      this.lazyRemoveLocal,
    );
  }

  private createSessionStorageMethodWithStorageSpace<
    T,
  >(): StorageMethodWithStorageSpace<T> {
    return this.createGenericStorageMethodWithStorageSpace<T>(
      this.lazyGetSession,
      this.lazySetSession,
      this.lazyRemoveSession,
    );
  }
}
